import type { ColumnFiltersState, Updater } from '@tanstack/react-table';
import type { UseQueryStatesKeysMap, Values } from 'nuqs';

import { createParser, parseAsArrayOf, parseAsString, useQueryState, useQueryStates } from 'nuqs';
import { useCallback, useMemo } from 'react';

import type { PaginationQuery } from '~/components';

import { SearchParamKeys } from '~/constants/url';

import { includes } from './arrays';

export const parseAsPagination = createParser<PaginationQuery>({
  parse: (value) => {
    const pageIndex = Number(value.split(':')[0]) - 1;
    const pageSize = Number(value.split(':')[1]);

    if (Number.isNaN(pageIndex) || Number.isNaN(pageSize) || pageIndex < 0 || !includes([10, 20, 30, 40, 50], pageSize)) return null;

    return { pageIndex, pageSize };
  },
  serialize: ({ pageIndex, pageSize }) => `${pageIndex + 1}:${pageSize}`,
  eq: (a, b) => a.pageIndex === b.pageIndex && a.pageSize === b.pageSize,
}).withDefault({ pageIndex: 0, pageSize: 10 });

const parseAsSorting = ({
  defaultSorting,
  sortingIdPrefix = '',
  sortingIdValidator,
}: {
  defaultSorting: { desc: boolean; id: string };
  sortingIdPrefix?: string;
  sortingIdValidator: (value: string) => boolean;
}) =>
  parseAsArrayOf(
    createParser({
      parse: (value) => {
        const sortingId = `${sortingIdPrefix}${value.split(':')[0]}`;
        const sortingDirection = value.split(':')[1];

        if (!sortingIdValidator(sortingId) || !includes(['asc', 'desc'], sortingDirection)) {
          return null;
        }

        return { id: sortingId, desc: sortingDirection === 'desc' };
      },
      serialize: ({ desc, id }) => `${id.slice(sortingIdPrefix.length)}:${desc ? 'desc' : 'asc'}`,
      eq: (a, b) => a.id === b.id && a.desc === b.desc,
    }),
  ).withDefault([defaultSorting]);

export const parseAsIntegerWithValidation = (validator: (value: number) => boolean) =>
  createParser({
    parse: (value) => {
      const integer = Number(value);

      if (Number.isNaN(integer) || !validator(integer)) return null;

      return integer;
    },
    serialize: (value) => value.toString(),
  });

export const parseAsStringWithValidation = <T extends string = string>(validator: (value: string) => boolean) =>
  createParser<T>({
    parse: (value) => {
      if (!validator(value)) return null;

      return value as T;
    },
    serialize: (value) => value,
  });

const parseAsGlobalFilter = parseAsString.withDefault('');

/** Maps each column filter's search-param name to the id of the column it filters. */
type ColumnFilterColumnIds = Record<string, string>;

/**
 * The column-filter state as TanStack holds it, kept as a union discriminated on `id` so that
 * `columnFilters.find(({ id }) => id === ColumnIds.x)?.value` stays narrowed to that column's own type.
 */
type TableColumnFilter<TColumnIds extends ColumnFilterColumnIds, TParsers extends UseQueryStatesKeysMap> = {
  [Key in keyof TColumnIds]: { id: TColumnIds[Key]; value: Key extends keyof TParsers ? Values<TParsers>[Key] : never };
}[keyof TColumnIds];

/**
 * Builds the four URL-synced state hooks a server-side table needs.
 *
 * `columnFilters` is static, which keeps the array/object mapping stable across renders.
 * `useColumnFilterParsers` is a hook so its parsers can validate against filter options that are fetched.
 */
export const createTableSearchParams = <
  TParsers extends UseQueryStatesKeysMap,
  const TColumnIds extends Record<keyof TParsers & string, string>,
>({
  columnFilters,
  defaultSorting,
  sortableColumnIds,
  sortingIdPrefix,
  useColumnFilterParsers,
}: {
  columnFilters: TColumnIds;
  defaultSorting: { desc: boolean; id: string };
  sortableColumnIds: string[];
  sortingIdPrefix: string;
  useColumnFilterParsers: () => TParsers;
}) => {
  type ColumnFilter = TableColumnFilter<TColumnIds, TParsers>;

  const columnFilterEntries = Object.entries(columnFilters) as [keyof TParsers & string, string][];

  // `Object.entries` erases the pairing between a search-param key and its column id, so the
  // discriminated union `TableColumnFilter` describes is restored here.
  const mapColumnFiltersToArray = (values: Values<TParsers>) =>
    columnFilterEntries
      .map(([key, columnId]) => ({ id: columnId, value: values[key] }))
      .filter(({ value }) => (Array.isArray(value) ? value.length > 0 : value !== null)) as ColumnFilter[];

  const mapColumnFiltersToObject = (columnFiltersState: ColumnFiltersState) =>
    Object.fromEntries(
      columnFilterEntries.map(([key, columnId]) => [key, columnFiltersState.find(({ id }) => id === columnId)?.value ?? null]),
    ) as Partial<Values<TParsers>>;

  /**
   * Controlled table pagination state, synced with the URL query string
   */
  const usePaginationState = () => useQueryState(SearchParamKeys.pagination, parseAsPagination);

  /**
   * Controlled table sorting state, synced with the URL query string
   */
  const useSorting = () =>
    useQueryState(
      SearchParamKeys.sorting,
      parseAsSorting({
        defaultSorting,
        sortingIdPrefix,
        sortingIdValidator: (value) => includes(sortableColumnIds, value),
      }),
    );

  /**
   * Controlled table global filter state, synced with the URL query string
   */
  const useGlobalFilter = () => useQueryState(SearchParamKeys.globalFilter, parseAsGlobalFilter);

  /**
   * Controlled table column filters state, synced with the URL query string
   *
   * (Internally, this state is mapped from an array to an object, which is easier to validate and persist in the URL.)
   */
  const useColumnFilters = () => {
    const [columnFiltersAsObject, setColumnFiltersAsObject] = useQueryStates(useColumnFilterParsers());

    const columnFiltersState = useMemo(() => mapColumnFiltersToArray(columnFiltersAsObject), [columnFiltersAsObject]);

    const setColumnFilter = useCallback(
      (updater: Updater<ColumnFiltersState> | null) => {
        const newValue = typeof updater === 'function' ? updater(mapColumnFiltersToArray(columnFiltersAsObject)) : updater;
        setColumnFiltersAsObject(newValue === null ? null : mapColumnFiltersToObject(newValue));
      },
      [columnFiltersAsObject, setColumnFiltersAsObject],
    );

    return [columnFiltersState, setColumnFilter] as const;
  };

  return { useColumnFilters, useGlobalFilter, usePaginationState, useSorting };
};
